import { PlatformError } from '@djgg/core';
import {
  canonicalDigest,
  canonicalJson,
  validateJsonObject,
} from '@djgg/gameplay-kit';
import {
  validateGameQualityContractV2,
  validateQualityArtifactRefV2,
  type GameQualityContractV2,
  type GameQualityRequirementV2,
  type QualityArtifactRefV2,
} from './qualitySchemas.js';
import { validateAcceptedCompositionV1, type AcceptedCompositionV1 } from './spec/gameSpec.js';

const SHA256 = /^[a-f0-9]{64}$/;
const ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

export interface CompositionConversionMappingV1 {
  sourceRequirementId: string;
  targetRequirementId: string;
  semanticDigest: string;
}

/** Immutable proof that a server-owned maintained composition preserves the
 * mandatory contract of one exact published legacy version. This record is
 * acceptance authority only. It never makes the legacy source executable. */
export interface CompositionConversionV1 {
  schemaVersion: 1;
  source: {
    creationId: string;
    version: number;
    qualityRunId: string;
    admissionDigest: string;
    specificationDigest: string;
    contractDigest: string;
    artifact: QualityArtifactRefV2;
  };
  target: {
    authorityDigest: string;
    runtimeReleaseId: string;
    compilerDigest: string;
    closureDigest: string;
  };
  mappings: CompositionConversionMappingV1[];
  digest: string;
}

export interface DeriveCompositionConversionInputV1 {
  creationId: string;
  version: number;
  qualityRunId: string;
  admissionDigest: string;
  specificationDigest: string;
  contractDigest: string;
  sourceArtifact: QualityArtifactRefV2;
  legacyContract: GameQualityContractV2;
  targetAuthority: AcceptedCompositionV1;
}

function unsupported(reason: string): never {
  throw new PlatformError('validation', `Legacy composition conversion unsupported: ${reason}`);
}

function exactKeys(value: Record<string, unknown>, path: string, keys: readonly string[]): void {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  if (canonicalJson(actual) !== canonicalJson(expected)) {
    throw new PlatformError('validation', `${path} has unsupported fields`);
  }
}

function stableId(value: unknown, path: string): string {
  if (typeof value !== 'string' || !ID.test(value)) {
    throw new PlatformError('validation', `${path} is invalid`);
  }
  return value;
}

function sha256(value: unknown, path: string): string {
  if (typeof value !== 'string' || !SHA256.test(value)) {
    throw new PlatformError('validation', `${path} is invalid`);
  }
  return value;
}

function positiveVersion(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || Number(value) < 1) {
    throw new PlatformError('validation', `${path} is invalid`);
  }
  return Number(value);
}

function semanticObligation(requirement: GameQualityRequirementV2): unknown {
  return {
    mandatory: requirement.mandatory,
    semantic: requirement.semantic,
    actors: requirement.actors,
    targets: requirement.targets,
    preconditions: requirement.preconditions,
    actions: requirement.actions,
    consequences: requirement.consequences,
    forbiddenOutcomes: requirement.forbiddenOutcomes,
    obligations: requirement.obligations,
  };
}

/** Derive the only allowed mapping. The caller supplies no mapping and cannot
 * approve a semantic amendment: a requirement in the already validated,
 * closed maintained profile must be byte-canonical semantically equivalent. */
export async function deriveCompositionConversionV1(
  input: DeriveCompositionConversionInputV1,
): Promise<CompositionConversionV1> {
  const legacyContract = validateGameQualityContractV2(input.legacyContract);
  const target = await validateAcceptedCompositionV1(input.targetAuthority);
  if (!target.runtimeReleaseId) unsupported('target authority is not an immutable released runtime');
  if (input.version < 1 || !Number.isSafeInteger(input.version)) unsupported('source version is invalid');
  if (!ID.test(input.creationId) || !ID.test(input.qualityRunId)) unsupported('source identity is invalid');
  if (![input.admissionDigest, input.specificationDigest, input.contractDigest].every(value => SHA256.test(value))) {
    unsupported('source quality identity is incomplete');
  }
  const sourceArtifact = validateQualityArtifactRefV2(input.sourceArtifact, 'compositionConversion.source.artifact');
  if (input.contractDigest !== await canonicalDigest(legacyContract)) {
    unsupported('stored legacy contract digest does not match its requirements');
  }

  const mandatory = legacyContract.requirements.filter(requirement => requirement.mandatory);
  if (!mandatory.length) unsupported('accepted legacy record has no mandatory requirements');
  const targetRequirements = target.intent.requirements.filter(requirement => requirement.mandatory);
  const claimedTargets = new Set<string>();
  const mappings: CompositionConversionMappingV1[] = [];
  for (const source of [...mandatory].sort((left, right) => left.id.localeCompare(right.id))) {
    const sourceSemantic = semanticObligation(source);
    const matches = targetRequirements.filter(candidate =>
      canonicalJson(semanticObligation(candidate)) === canonicalJson(sourceSemantic));
    if (matches.length !== 1) {
      unsupported(`mandatory requirement ${source.id} has no unique exact retained target obligation`);
    }
    const matched = matches[0]!;
    if (claimedTargets.has(matched.id)) unsupported(`target obligation ${matched.id} is reused`);
    claimedTargets.add(matched.id);
    mappings.push({
      sourceRequirementId: source.id,
      targetRequirementId: matched.id,
      semanticDigest: await canonicalDigest(sourceSemantic),
    });
  }

  const body: Omit<CompositionConversionV1, 'digest'> = {
    schemaVersion: 1,
    source: {
      creationId: input.creationId,
      version: input.version,
      qualityRunId: input.qualityRunId,
      admissionDigest: input.admissionDigest,
      specificationDigest: input.specificationDigest,
      contractDigest: input.contractDigest,
      artifact: sourceArtifact,
    },
    target: {
      authorityDigest: target.digest,
      runtimeReleaseId: target.runtimeReleaseId,
      compilerDigest: target.compilerDigest,
      closureDigest: target.closureDigest,
    },
    mappings,
  };
  return { ...structuredClone(body), digest: await canonicalDigest(body) };
}

export async function validateCompositionConversionV1(value: unknown): Promise<CompositionConversionV1> {
  const conversion = validateJsonObject(value, 'compositionConversion');
  exactKeys(conversion, 'compositionConversion', ['schemaVersion', 'source', 'target', 'mappings', 'digest']);
  if (conversion.schemaVersion !== 1) throw new PlatformError('validation', 'Unsupported composition conversion version');
  const source = validateJsonObject(conversion.source, 'compositionConversion.source');
  exactKeys(source, 'compositionConversion.source', [
    'creationId', 'version', 'qualityRunId', 'admissionDigest', 'specificationDigest', 'contractDigest', 'artifact',
  ]);
  const target = validateJsonObject(conversion.target, 'compositionConversion.target');
  exactKeys(target, 'compositionConversion.target', [
    'authorityDigest', 'runtimeReleaseId', 'compilerDigest', 'closureDigest',
  ]);
  if (!Array.isArray(conversion.mappings) || conversion.mappings.length < 1 || conversion.mappings.length > 128) {
    throw new PlatformError('validation', 'Composition conversion requires 1..128 mappings');
  }
  const sourceIds = new Set<string>();
  const targetIds = new Set<string>();
  const mappings = conversion.mappings.map((entry, index): CompositionConversionMappingV1 => {
    const mapping = validateJsonObject(entry, `compositionConversion.mappings[${index}]`);
    exactKeys(mapping, `compositionConversion.mappings[${index}]`, [
      'sourceRequirementId', 'targetRequirementId', 'semanticDigest',
    ]);
    const sourceRequirementId = stableId(mapping.sourceRequirementId, `compositionConversion.mappings[${index}].sourceRequirementId`);
    const targetRequirementId = stableId(mapping.targetRequirementId, `compositionConversion.mappings[${index}].targetRequirementId`);
    if (sourceIds.has(sourceRequirementId) || targetIds.has(targetRequirementId)) {
      throw new PlatformError('validation', 'Composition conversion mappings must be one-to-one');
    }
    sourceIds.add(sourceRequirementId);
    targetIds.add(targetRequirementId);
    return { sourceRequirementId, targetRequirementId,
      semanticDigest: sha256(mapping.semanticDigest, `compositionConversion.mappings[${index}].semanticDigest`) };
  });
  const checked: Omit<CompositionConversionV1, 'digest'> = {
    schemaVersion: 1,
    source: {
      creationId: stableId(source.creationId, 'compositionConversion.source.creationId'),
      version: positiveVersion(source.version, 'compositionConversion.source.version'),
      qualityRunId: stableId(source.qualityRunId, 'compositionConversion.source.qualityRunId'),
      admissionDigest: sha256(source.admissionDigest, 'compositionConversion.source.admissionDigest'),
      specificationDigest: sha256(source.specificationDigest, 'compositionConversion.source.specificationDigest'),
      contractDigest: sha256(source.contractDigest, 'compositionConversion.source.contractDigest'),
      artifact: validateQualityArtifactRefV2(source.artifact, 'compositionConversion.source.artifact'),
    },
    target: {
      authorityDigest: sha256(target.authorityDigest, 'compositionConversion.target.authorityDigest'),
      runtimeReleaseId: sha256(target.runtimeReleaseId, 'compositionConversion.target.runtimeReleaseId'),
      compilerDigest: sha256(target.compilerDigest, 'compositionConversion.target.compilerDigest'),
      closureDigest: sha256(target.closureDigest, 'compositionConversion.target.closureDigest'),
    },
    mappings,
  };
  const digest = sha256(conversion.digest, 'compositionConversion.digest');
  if (digest !== await canonicalDigest(checked)) {
    throw new PlatformError('validation', 'Composition conversion digest mismatch');
  }
  return { ...structuredClone(checked), digest };
}

export async function assertCompositionConversionTargetV1(
  conversionValue: unknown,
  authorityValue: unknown,
): Promise<CompositionConversionV1> {
  const conversion = await validateCompositionConversionV1(conversionValue);
  const authority = await validateAcceptedCompositionV1(authorityValue);
  if (!authority.runtimeReleaseId
    || conversion.target.authorityDigest !== authority.digest
    || conversion.target.runtimeReleaseId !== authority.runtimeReleaseId
    || conversion.target.compilerDigest !== authority.compilerDigest
    || conversion.target.closureDigest !== authority.closureDigest) {
    throw new PlatformError('conflict', 'Composition conversion target authority changed');
  }
  return conversion;
}
